/**
 * outdated-alert.js
 */

import React from 'react';
import { useMediaQuery, useTheme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Alert } from '@mui/material';
import { parseISO } from 'date-fns';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { UserContext } from 'contexts/user/user-context';
import ExerciseViewBase from 'components/training/exercise/view/exercise-view-base';


const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3)
  },
  diffContainer: {
    fontSize: theme.typography.sizes[1],
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.sys.outline}`,
    borderRadius: 4,
    overflow: 'hidden',
    '& table pre': {
      lineHeight: '20px'
    },
    '& table a': {
      fontSize: theme.typography.sizes[1],
      fontWeight: 'normal'
    }
  },
  removed: {
    backgroundColor: theme.palette.comp.outdatedAlert.removedBackground,
    padding: `0 3px`,
    borderRadius: 2,
  },
  added: {
    backgroundColor: theme.palette.comp.outdatedAlert.addedBackground,
    padding: `0 3px`,
    borderRadius: 2,
  }
}));


export default function OutdatedAlert(props) {
  const { answer, exercise } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('sm'));
  const [latestExerText, setLatestExerText] = React.useState();
  const answerId = answer.id;
  const isOutdated = answer.isOutdated;
  const customStyles = {
    variables: {
      dark: {
        wordRemovedBackground: theme.palette.comp.outdatedAlert.removedBackground,
        wordAddedBackground: theme.palette.comp.outdatedAlert.addedBackground,
      },
      light: {
        wordRemovedBackground: theme.palette.comp.outdatedAlert.removedBackground,
        wordAddedBackground: theme.palette.comp.outdatedAlert.addedBackground,
      }
    }
  };


  const removeEmptyLines = (string) => {
    return string.replace(/^\s*$(?:\r\n?|\n)/gm, '');
  }


  React.useLayoutEffect(() => {
    if (isOutdated) {
      setLatestExerText();
    } else {
      setLatestExerText(false);
    }
  }, [answerId, isOutdated]);


  React.useLayoutEffect(() => {
    if (latestExerText === undefined) {
      const renderContainer = document.getElementById('latest-exer-render');
      if (renderContainer && Boolean(renderContainer.innerText)) {
        setLatestExerText(removeEmptyLines(renderContainer.innerText));
      }
    }
  }, [latestExerText]);
  

  if (!isOutdated || !user.role.isHandler) return null;


  // get the user who last edited the exercise
  const recentEditUser = exercise.versions[exercise.versions.length - 1].user;

  // get the exercise version just before the last modified date of answer
  const answerModDate = parseISO(answer.dateModified);
  const relevantVersionText = removeEmptyLines(exercise.versions.find(v => answerModDate < parseISO(v.dateChanged)).render);

  const areVersionsIdentical = relevantVersionText === latestExerText;

  
  return (
    <>
      {Boolean(latestExerText) &&
      <Alert
        severity='warning'
        data-testid='record-invalid-alert'
        className={classes.root}
      >
        <Typography paragraph={!areVersionsIdentical}>
          {areVersionsIdentical &&
          <>
            {recentEditUser.id === user.id ? 'You' : recentEditUser.name} modified the exercise details after you saved this completion and it is now outdated. 
            The exercise changes have since been reverted but you still need to verify the update. 
            Edit this record and then click Save to remove the 'Outdated' status.
          </>}

          {!areVersionsIdentical &&
          <>
            {recentEditUser.id === user.id ? 'You' : recentEditUser.name} modified the exercise details after you saved this completion record. 
            Your completion is now outdated and needs to be verified. 
            The changes are shown below.
            <br /><br />
            {isXS &&
            <>
              <span className={classes.removed}>Red text</span> shows how the exercise looked when you completed it.<br />
              <span className={classes.added}>Green text</span> shows how the exercise looks now.
            </>}
            {!isXS &&
            <>
              <span className={classes.removed}>Red text</span> on the left shows how the exercise looked when you completed it.<br />
              <span className={classes.added}>Green text</span> on the right shows how the exercise looks now.
            </>}
            <br /><br />
            Please edit your record, make any changes (if required) and then click Save. 
            Resaving your completion record signifies that you agree with the exercise modifications.
          </>}
        </Typography>

        {!areVersionsIdentical &&
        <div className={classes.diffContainer}>
          <ReactDiffViewer
            styles={customStyles}
            useDarkTheme={theme.palette.isDarkMode}
            oldValue={relevantVersionText}
            newValue={latestExerText}
            splitView={!isXS}
            hideLineNumbers={true}
            compareMethod={DiffMethod.SENTENCES}
            codeFoldMessageRenderer={n => `Show ${n} more line${n === 1 ? '' : 's'} ...`}
          />
        </div>}
      </Alert>}

      <div id='latest-exer-render' style={{ height: 1, overflow: 'hidden' }}>
        <ExerciseViewBase exerciseRecord={exercise} showName />
      </div>
    </>
  );
}